import React from 'react';
import { ExternalLink } from 'lucide-react';
import { PayPeriod, LineItem, CommissionModel } from '../types';
import { formatCurrency, formatDateRange, formatDate } from '../utils/formatting';
interface Column {
  key: string;
  label: string;
  align?: 'left' | 'right';
  render: (item: LineItem) => React.ReactNode;
}
interface InnerTableProps {
  columns: Column[];
  items: LineItem[];
  footer?: React.ReactNode;
}
// Shared table body used by the dense Commissions / Expense tables.
function InnerTable({
  columns,
  items,
  footer
}: InnerTableProps) {
  return <div className="overflow-x-auto" data-id="element-27">
      <table className="w-full text-sm" data-id="element-28">
        <thead className="bg-white border-b border-slate-100" data-id="element-29">
          <tr data-id="element-30">
            {columns.map(col => <th key={col.key} className={`px-2.5 py-2 font-medium text-slate-500 text-[11px] uppercase tracking-wide whitespace-nowrap ${col.align === 'right' ? 'text-right' : 'text-left'}`} data-id="element-31">
                {col.label}
              </th>)}
          </tr>
        </thead>
        <tbody data-id="element-32">
          {items.map(item => <tr key={item.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50" data-id="element-33">
              {columns.map(col => <td key={col.key} className={`px-2.5 py-2 whitespace-nowrap text-xs ${col.align === 'right' ? 'text-right text-slate-700' : 'text-slate-700'}`} data-id="element-34">
                  {col.render(item)}
                </td>)}
            </tr>)}
        </tbody>
        {footer}
      </table>
    </div>;
}
function SubtotalFooter({
  columns,
  amountNode
}: {
  columns: Column[];
  amountNode: React.ReactNode;
}) {
  return <tfoot className="bg-slate-50 border-t border-slate-200" data-id="element-35">
      <tr data-id="element-36">
        <td colSpan={Math.max(1, columns.length - 1)} className="px-2.5 py-2 text-right text-xs font-semibold uppercase tracking-wide text-slate-600" data-id="element-37">
          Subtotal
        </td>
        <td className="px-2.5 py-2 text-right text-sm font-semibold whitespace-nowrap" data-id="element-38">
          {amountNode}
        </td>
      </tr>
    </tfoot>;
}
interface ExpandedRowProps {
  payPeriods: PayPeriod[];
  commissionModel: CommissionModel;
}
// Payment types that roll up into the combined "Commissions" section.
const COMMISSION_TYPES = ['Payment', 'Manager Override', 'Referral Payment', 'Recruiting Bonus'];
interface CommissionsTableProps {
  items: LineItem[];
}
function CommissionsTable({
  items
}: CommissionsTableProps) {
  if (items.length === 0) return null;
  const columns: Column[] = [{
    key: 'loan',
    label: 'Loan',
    render: item => <a href="#" onClick={e => {
      e.preventDefault();
      console.log('Open opportunity:', item.opportunityId);
    }} className="text-blue-600 hover:text-blue-700 hover:underline whitespace-nowrap" data-id="element-39">
          {item.opportunityName}
        </a>
  }, {
    key: 'gross',
    label: 'Gross Commission',
    align: 'right',
    render: i => i.gross != null ? formatCurrency(i.gross) : '—'
  }, {
    key: 'net',
    label: 'Net Commission',
    align: 'right',
    render: i => {
      const net = i.net ?? i.managerNet ?? i.amount;
      return <span className="font-semibold text-slate-900" data-id="element-40">
            {formatCurrency(net)}
          </span>;
    }
  }];
  const subtotal = items.reduce((sum, i) => sum + (i.net ?? i.managerNet ?? i.amount), 0);
  return <div className="bg-white rounded-lg border border-slate-200 overflow-hidden" data-id="element-41">
      <div className="flex items-center justify-between px-4 py-2 bg-slate-50 border-b border-slate-200" data-id="element-42">
        <div className="flex items-center gap-2" data-id="element-43">
          <h4 className="text-sm font-semibold text-slate-900" data-id="element-44">Commissions</h4>
          <span className="px-2 py-0.5 bg-white border border-slate-200 text-slate-600 rounded-full text-xs font-medium" data-id="element-45">
            {items.length}
          </span>
        </div>
      </div>
      <InnerTable columns={columns} items={items} footer={<SubtotalFooter columns={columns} amountNode={<span className="text-slate-900" data-id="element-48">{formatCurrency(subtotal)}</span>} data-id="element-47" />} data-id="element-46" />
    </div>;
}
interface ExpenseTableProps {
  items: LineItem[];
}
function ExpenseTable({
  items
}: ExpenseTableProps) {
  if (items.length === 0) return null;
  const columns: Column[] = [{
    key: 'item',
    label: 'Item',
    render: i => <span className="font-medium text-slate-900 whitespace-nowrap" data-id="element-49">
          {i.itemName ?? i.opportunityName}
        </span>
  }, {
    key: 'expenseCategory',
    label: 'Expense Category',
    render: i => i.expenseCategory ? <span className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded-full text-xs font-medium" data-id="element-50">
            {i.expenseCategory}
          </span> : '—'
  }, {
    key: 'expenseDate',
    label: 'Expense Date',
    render: i => i.expenseDate ? formatDate(i.expenseDate) : '—'
  }, {
    key: 'expenseDetail',
    label: 'Expense Detail',
    render: i => <span className="text-slate-600" data-id="element-51">{i.expenseDetail ?? '—'}</span>
  }, {
    key: 'expenseAmount',
    label: 'Expense Amount',
    align: 'right',
    render: i => <span className="font-semibold text-red-600" data-id="element-52">
          −{formatCurrency(i.amount)}
        </span>
  }];
  const subtotal = items.reduce((sum, i) => sum + i.amount, 0);
  return <div className="bg-white rounded-lg border border-slate-200 overflow-hidden" data-id="element-53">
      <div className="flex items-center justify-between px-4 py-2 bg-slate-50 border-b border-slate-200" data-id="element-54">
        <div className="flex items-center gap-2" data-id="element-55">
          <h4 className="text-sm font-semibold text-slate-900" data-id="element-56">Expense</h4>
          <span className="px-2 py-0.5 bg-white border border-slate-200 text-slate-600 rounded-full text-xs font-medium" data-id="element-57">
            {items.length}
          </span>
        </div>
      </div>
      <InnerTable columns={columns} items={items} footer={<SubtotalFooter columns={columns} amountNode={<span className="text-red-600" data-id="element-60">−{formatCurrency(subtotal)}</span>} data-id="element-59" />} data-id="element-58" />
    </div>;
}
export function ExpandedRow({
  payPeriods
}: ExpandedRowProps) {
  return <div className="px-6 py-4 bg-slate-50 border-t border-slate-200" data-id="element-61">
      <div className="space-y-8" data-id="element-62">
        {payPeriods.map(period => {
        const commissionItems = period.lineItems.filter(li => COMMISSION_TYPES.includes(li.paymentType));
        const expenses = period.lineItems.filter(li => li.paymentType === 'Expense');
        const totalNetCommission = period.lineItems.reduce((sum, li) => li.paymentType === 'Expense' ? sum - li.amount : sum + li.amount, 0);
        const totalPayrollAmount = totalNetCommission;
        return <div key={period.id} className="space-y-3" data-id="element-63">
              <div className="flex items-center gap-3 flex-wrap" data-id="element-64">
                <span className="px-3 py-1 bg-slate-200 text-slate-700 rounded-full text-sm font-semibold" data-id="element-65">
                  {formatDateRange(period.startDate, period.endDate)}
                </span>
                <span className="px-3 py-1 bg-white border border-slate-200 rounded-full text-sm" data-id="element-66">
                  Closed Units:{' '}
                  <span className="font-semibold" data-id="element-67">{period.units}</span>
                </span>
                <span className="px-3 py-1 bg-white border border-slate-200 rounded-full text-sm" data-id="element-68">
                  Closed Loan Volume:{' '}
                  <span className="font-semibold" data-id="element-69">
                    {formatCurrency(period.loanAmount)}
                  </span>
                </span>
                <a href="#" onClick={e => {
              e.preventDefault();
              console.log('Open pay period:', period.id);
            }} className="text-blue-600 hover:text-blue-700 flex items-center gap-1 text-sm" aria-label="Open pay period" data-id="element-70">
                  <ExternalLink className="w-4 h-4" data-id="element-71" />
                </a>
              </div>

              <div className="space-y-3" data-id="element-72">
                <CommissionsTable items={commissionItems} data-id="element-73" />
                <ExpenseTable items={expenses} data-id="element-74" />
              </div>

              {/* Totals */}
              <div className="bg-white rounded-lg border border-slate-200 overflow-hidden mt-3" data-id="element-75">
                <div className="divide-y divide-slate-100" data-id="element-76">
                  <div className="flex items-center justify-between px-4 py-3" data-id="element-77">
                    <span className="text-sm font-medium text-slate-600" data-id="element-78">
                      Total Net Commission
                    </span>
                    <span className="text-sm font-bold text-slate-900" data-id="element-79">
                      {formatCurrency(totalNetCommission)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between px-4 py-3 bg-slate-50" data-id="element-80">
                    <span className="text-sm font-semibold text-slate-700" data-id="element-81">
                      Total Payroll Amount
                    </span>
                    <span className="text-base font-bold text-slate-900" data-id="element-82">
                      {formatCurrency(totalPayrollAmount)}
                    </span>
                  </div>
                </div>
              </div>
            </div>;
      })}
      </div>
    </div>;
}